import {useCallback, useMemo, useState} from 'react';

import parseRowCount from 'notebook/sample-propagation/sample-selection/parseRowCount';
import {RowCountDetails} from 'notebook/sample-propagation/sample-selection/RowCountDetails';

/**
 * Track the number of rows to add for each sample.
 *
 * By using this hook, you don't need to think about:
 * - **How to track row counts.** This hook tracks row counts by the index of the row's sample in sampleKeys.
 * - **How to implement a default row count.** This hook initializes all row counts at DEFAULT_ROW_COUNT.
 *
 * @param numSamples Number of samples to track row counts for.
 */
export default function useSampleRowCounts(
  numSamples: number,
  rowCountSettings: {
    minRowCount: number;
    defaultRowCount: number;
    maxRowCount: number;
  }
): {
  rowCountDetailsByIndex: Array<RowCountDetails>;
  bulkSetRowCountText: (newRowCountText: string) => void;
  resetSampleRowCounts: () => void;
  setRowCountText: (sampleIndex: number, rowCountText: string) => void;
} {
  const {rowCountTextByIndex, bulkSetRowCountText, resetAllRowCountTexts, setRowCountText} =
    useSampleRowCountTextWithDefault(numSamples, rowCountSettings.defaultRowCount);

  const rowCountDetailsByIndex = rowCountTextByIndex.map((rowCountText) =>
    parseRowCount(rowCountText, rowCountSettings.minRowCount, rowCountSettings.maxRowCount)
  );

  return {
    rowCountDetailsByIndex,
    bulkSetRowCountText,
    resetSampleRowCounts: resetAllRowCountTexts,
    setRowCountText,
  };
}

/**
 * Track row counts as text, defaulting to `defaultRowCount`.
 * @param sampleKeys Keys of samples to track row counts for.
 */
function useSampleRowCountTextWithDefault(
  numSamples: number,
  defaultRowCount: number
): {
  rowCountTextByIndex: Array<string>;
  bulkSetRowCountText: (newRowCountText: string) => void;
  resetAllRowCountTexts: () => void;
  setRowCountText: (sampleIndex: number, rowCountText: string) => void;
} {
  // - Track the text in the input box, not the parsed number. By tracking text,
  //   we can keep track of the input's contents even when it's not a valid number.
  const [rowCountTextOverrides, setRowCountTextOverrides] = useState<Array<string>>([]);

  const rowCountTextByIndex = useMemo(
    () =>
      [...rowCountTextOverrides, ...Array(numSamples)]
        .slice(0, numSamples)
        .map((value) => value ?? String(defaultRowCount)),
    [defaultRowCount, numSamples, rowCountTextOverrides]
  );

  return {
    rowCountTextByIndex,
    bulkSetRowCountText: useCallback(
      (newRowCountText: string) => {
        setRowCountTextOverrides(Array(numSamples).fill(newRowCountText));
      },
      [numSamples]
    ),
    resetAllRowCountTexts: useCallback(() => {
      setRowCountTextOverrides([]);
    }, []),
    setRowCountText: (sampleIndex: number, rowCountText: string) => {
      const newRowCountText = [...rowCountTextByIndex];
      newRowCountText[sampleIndex] = rowCountText;
      setRowCountTextOverrides(newRowCountText);
    },
  };
}
